import { useCart } from '../contexts/CartContext';
import { Link, useNavigate } from 'react-router-dom';

type CartPanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default function CartPanel({ isOpen, onClose }: CartPanelProps) {
  const { items, removeItem, updateQuantity, subtotal, totalItems } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-[90] transition-opacity"
          onClick={onClose}
        ></div>
      )}

      {/* Cart Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[450px] bg-[#1A1A1A] z-[100] transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold uppercase tracking-wider">
            Shopping Cart ({totalItems})
          </h2>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center mb-6 border border-white/20 rounded-full">
              <i className="ri-shopping-bag-line text-3xl text-white/40"></i>
            </div>
            <p className="text-white/60 text-lg mb-2">Your cart is empty</p>
            <p className="text-white/40 text-sm mb-6">
              Add some merch to get started
            </p>
            <Link
              to="/merch"
              onClick={onClose}
              className="px-8 py-3 bg-[#8B6F47] text-black text-sm uppercase tracking-wider hover:bg-[#A08558] transition-colors cursor-pointer whitespace-nowrap font-medium"
            >
              Shop Merch
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {items.map((item) => (
                <div
                  key={`${item.id}-${item.size || 'no-size'}`}
                  className="flex gap-4 bg-[#1A1A1A] p-4"
                >
                  <div className="w-24 h-24 flex-shrink-0 bg-[#0A0A0A]">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-sm mb-1 truncate">
                      {item.name}
                    </h3>
                    {item.size && (
                      <p className="text-white/50 text-xs mb-2">Size: {item.size}</p>
                    )}
                    <p className="text-[#8B6F47] font-bold text-sm mb-3">
                      ${item.price.toFixed(2)}
                    </p>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center border border-white/20">
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.size, item.quantity - 1)
                          }
                          className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line"></i>
                        </button>
                        <span className="w-10 text-center text-sm">{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item.id, item.size, item.quantity + 1)
                          }
                          className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line"></i>
                        </button>
                      </div>

                      <button
                        onClick={() => removeItem(item.id, item.size)}
                        className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10 bg-[#0A0A0A]">
              <div className="flex justify-between items-center mb-6">
                <span className="text-lg uppercase tracking-wider">Subtotal</span>
                <span className="text-2xl font-bold text-[#8B6F47]">
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full px-8 py-4 bg-[#8B6F47] text-black text-sm uppercase tracking-wider hover:bg-[#A08558] transition-colors cursor-pointer whitespace-nowrap font-medium"
              >
                Proceed to Checkout
              </button>
              <button
                onClick={onClose}
                className="w-full px-8 py-4 mt-3 bg-white/5 text-white text-sm uppercase tracking-wider hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
